/**
 * Centralized logging utility with DEBUG_LEVEL support
 */

export enum LogLevel {
  TRACE = 0,
  DEBUG = 1,
  WARN = 2,
  ERROR = 3,
}

export interface LoggerConfig {
  level: LogLevel;
  prefix?: string;
}

class Logger {
  private config: LoggerConfig;

  constructor(config: LoggerConfig) {
    this.config = config;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.config.level;
  }

  private formatMessage(level: string, message: string, ...args: any[]): string {
    const timestamp = new Date().toISOString();
    const prefix = this.config.prefix ? `[${this.config.prefix}]` : "";
    return `${timestamp} ${prefix} [${level}] ${message}`;
  }

  trace(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.TRACE)) {
      console.trace(this.formatMessage("TRACE", message), ...args);
    }
  }

  debug(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(this.formatMessage("DEBUG", message), ...args);
    }
  }

  warn(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage("WARN", message), ...args);
    }
  }

  error(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage("ERROR", message), ...args);
    }
  }

  info(message: string, ...args: any[]): void {
    // INFO is treated as DEBUG level
    this.debug(message, ...args);
  }
}

// Parse DEBUG_LEVEL from environment
function parseLogLevel(): LogLevel {
  const debugLevel = process.env.REACT_APP_DEBUG_LEVEL || "DEBUG";

  switch (debugLevel.toUpperCase()) {
    case "TRACE":
      return LogLevel.TRACE;
    case "DEBUG":
      return LogLevel.DEBUG;
    case "WARN":
      return LogLevel.WARN;
    case "ERROR":
      return LogLevel.ERROR;
    default:
      console.warn(`Invalid DEBUG_LEVEL: ${debugLevel}. Defaulting to DEBUG.`);
      return LogLevel.DEBUG;
  }
}

// Create default logger instance
export const logger = new Logger({
  level: parseLogLevel(),
});

// Create logger factory for specific modules
export function createLogger(prefix: string): Logger {
  return new Logger({
    level: parseLogLevel(),
    prefix,
  });
}
